<template>
  <div style="background-color: red">
    <!-- INTRODUCTION -->
    <content-section theme="light" noPaddingBottom>
      <b-row>
        <b-col sm="12" md="6" class="text-center" align-self="center">
          <p class="mb-0">Hello, my name is</p>
          <h1 class="mt-1">Yoseph Martin Christian</h1>
          <p>Welcome to my home on the internet</p>
        </b-col>
        <b-col sm="12" md="6" class="pb-0">
          <b-img
            :src="yosephMartinChristianPrimaryPhoto"
            fluid
            alt="Yoseph Martin Christian - primary photo"
          ></b-img>
        </b-col>
      </b-row>
    </content-section>
    <!-- SUMMARY -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>A Summary of Myself</h1>
          <p>Let me give you the short version of my story</p>
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="6" class="text-justify">
          <p>
            I’m a software engineer with a passion for building clean,
            user-friendly websites and mobile applications. My coding journey
            began at 14, and since then, I’ve been driven by a deep curiosity
            for how things work behind the screen.
          </p>
          <p>
            What sparked my interest was realizing that all the digital tools I
            loved were powered by code. That realization led me to explore
            various programming languages, development frameworks, and emerging
            technologies.
          </p>
          <p>
            Today, I enjoy solving problems through code, experimenting with new
            ideas, and continuously learning. I’m not afraid to break things to
            understand them better—as long as I’ve got a backup, of course.
          </p>
        </b-col>
        <b-col sm="12" md="6" align-self="center">
          <b-img
            class="personal-image center-personal-image"
            :src="yosephMartinChristianSecondaryPhoto"
            fluid
            alt="Yoseph Martin Christian - secondary photo"
          ></b-img>
        </b-col>
      </b-row>
    </content-section>
    <!-- ACADEMIC BACKGROUND -->
    <content-section theme="light">
      <b-row>
        <b-col class="text-center" sm="12">
          <h1>Academic Background</h1>
          <p>A quick look at my academic journey and achievements</p>
        </b-col>
        <b-col sm="12" md="4">
          <h2 class="academic-section-title">
            Bachelor of Science (Honors) in Software Engineering
          </h2>
          <h3>
            Asia Pacific University, Malaysia & Staffordshire University, UK
            (dual degree)
          </h3>
        </b-col>
        <b-col sm="12" md="4">
          <h2 class="academic-section-title">Graduation date</h2>
          <h3>21st December 2020</h3>
        </b-col>
        <b-col sm="12" md="4">
          <h2 class="academic-section-title">Cumulative GPA</h2>
          <h3>3.76 / 4.00 (First Class)</h3>
        </b-col>
      </b-row>
    </content-section>
    <!-- TECHNICAL KNOW-HOW -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>Technical Know-How</h1>
          <p>
            Always learning, always building. Here are some of the technologies
            I’ve worked with and gained hands-on experience in
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 1 -->
          <info-card id="website-development-technical-skills" theme="dark">
            <h2 slot="title">Website Development</h2>
            <p slot="tagline">Building the skills that power the modern web</p>
            <b-img
              center
              slot="custom-image"
              :src="websiteDevelopmentImage"
              fluid
              alt="Website Development Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">
                Technologies I’ve worked with: HTML5, CSS3 and Sass, JavaScript,
                TypeScript, Vue.js, ReactJS, Bootstrap, Node.js, Jest (Unit
                Testing)
              </p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>
                  My personal website –
                  <span class="highlighted">yosephmartin.com</span> (You are
                  here)
                </li>
                <li>
                  Stuff To-Do (<span class="highlighted">mystufftodo.com</span>)
                </li>
                <li>TravelJournal</li>
                <li>Disaster Escape Plan</li>
                <li>TutorMe</li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 2 -->
          <info-card
            id="mobile-application-development-technical-skills"
            theme="dark"
          >
            <h2 slot="title">Mobile Application Development</h2>
            <p slot="tagline">
              Creating apps designed to live right on your phone
            </p>
            <b-img
              center
              slot="custom-image"
              :src="mobileApplicationDevelopmentImage"
              fluid
              alt="Mobile Application Development Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">
                Technologies I’ve worked with: React Native with TypeScript
                (Cross-platform), Java (Android), Kotlin (Android), Swift (iOS),
                Flutter (Cross-platform), Jest (Unit Testing)
              </p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>
                  Stuff To-Do (<span class="highlighted">mystufftodo.com</span>)
                </li>
                <li>ProLog</li>
                <li>BodyMindfulness</li>
                <li>Clima</li>
                <li>Xylophone</li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 3 -->
          <info-card
            id="desktop-application-development-technical-skills"
            theme="dark"
          >
            <h2 slot="title">Desktop Application Development</h2>
            <p slot="tagline">
              Building executable apps designed to run right on your computer
            </p>
            <b-img
              center
              slot="custom-image"
              :src="desktopApplicationDevelopmentImage"
              fluid
              alt="Desktop Application Development Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">
                Technologies I’ve worked with: ASP.NET Core, C++, C#, Java,
                Python
              </p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>JustSubmit</li>
                <li>University Classroom Timetable System</li>
                <li>Matrix Manipulator</li>
                <li>Apartment Reservation System</li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 4 -->
          <info-card
            id="database-and-server-side-technical-skills"
            theme="dark"
          >
            <h2 slot="title">Database & Server Side</h2>
            <p slot="tagline">
              A beautiful front-end is only complete with a solid, functional
              back-end behind it
            </p>
            <b-img
              center
              slot="custom-image"
              :src="databaseAndServerSideImage"
              fluid
              alt="Database and Server Side Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">
                Technologies I’ve worked with: PHP, MySQL, Firebase, Microsoft
                Azure
              </p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>
                  Stuff To-Do (<span class="highlighted">mystufftodo.com</span>)
                </li>
                <li>JustSubmit</li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 5 -->
          <info-card
            id="game-application-development-technical-skills"
            theme="dark"
          >
            <h2 slot="title">Game Application Development</h2>
            <p slot="tagline">
              Why just play games when you can build your own?
            </p>
            <b-img
              center
              slot="custom-image"
              :src="gameApplicationDevelopmentImage"
              fluid
              alt="Game Application Development Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">Technologies I’ve worked with: Unity</p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>Galaxy Shooter</li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- TECHNICAL KNOW-HOW 6 -->
          <info-card id="version-control-technical-skills" theme="dark">
            <h2 slot="title">Version Control</h2>
            <p slot="tagline">
              Because every good change starts with a backup you can rely on
            </p>
            <b-img
              center
              slot="custom-image"
              :src="versionControlImage"
              fluid
              alt="Version Control Image"
            ></b-img>
            <div slot="description">
              <p class="highlighted">
                Technologies I’ve worked with: Git, GitHub, GitLab, BitBucket,
                Sourcetree
              </p>
              <p>Projects that use one or more of these technologies:</p>
              <ul>
                <li>
                  Most of the projects listed above use one or more of the
                  technologies mentioned
                </li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
    </content-section>
    <!-- HIGHLIGHTED PROJECTS -->
    <content-section theme="light">
      <b-row>
        <b-col class="text-center">
          <h1>Highlighted Projects</h1>
          <p>
            Welcome to a showcase of some of my best software projects to date
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 1 -->
          <info-card id="ymc-project">
            <h2 slot="title">My Personal Website</h2>
            <p slot="tagline">
              Very first effort to put my mark on the world wide web |
              <strong>https://yosephmartin.com</strong>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in ymcCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                What started back in 2018 as a simple way to document my
                development journey has grown into something much bigger. Over
                the years, my personal website has gone through several major
                updates, evolving alongside me.
              </p>
              <p>
                Every time I hit a milestone—big or small—this site is the first
                to know. What began as a space to showcase achievements has
                slowly become something more personal, a reflection of who I am
                and what I care about.
              </p>
              <p>
                Feel free to look around—this is my little corner of the
                internet. Hope you enjoy your stay!
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Vue.js, BootstrapVue, cPanel
              </p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              ><strong>Look who’s here—awesome!</strong>
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 2 -->
          <info-card id="stufftodo-project">
            <h2 slot="title">Stuff To-Do</h2>
            <p slot="tagline">
              Every one of us has a lot of #StuffToDo |
              <strong>https://mystufftodo.com</strong>
            </p>
            <b-img
              center
              slot="custom-image"
              :src="stuffToDoImage"
              fluid
              alt="Stuff To-Do"
            ></b-img>
            <div slot="description">
              <p>
                Stuff To-Do is a cross-platform to-do list app built for
                collaboration, available on both web and Android. It features
                real-time data sync across platforms, so your tasks stay
                up-to-date no matter where you use it.
              </p>
              <p>
                You can easily manage personal tasks or work with others on
                shared lists. The personal list lets you create and organize
                your own to-dos just the way you like them.
              </p>
              <p>
                But the real magic is in the collaborative list—it’s the heart
                of Stuff To-Do. Designed with teamwork in mind, it includes
                collaborator role management to make working together smoother
                and more organized.
              </p>
              <p>
                With Stuff To-Do, we’re aiming to change the way you see to-do
                lists. It’s more than just checking boxes—it’s a smarter, more
                connected way to get things done.
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Vue.js, BootstrapVue, Android,
                Java, XML, cPanel, Firebase Firestore
              </p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="openLink('https://mystufftodo.com')"
              >Access it on <strong>mystufftodo.com</strong>
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 3 -->
          <info-card>
            <h2 slot="title">Galaxy Shooter</h2>
            <p slot="tagline">
              A futuristic sci-fi galactic shooter against the unknown enemy in
              the deep space
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in galaxyShooterCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                Galaxy Shooter is a fast-paced 2D arcade game set in outer
                space, where players engage in thrilling spaceship battles. The
                game was developed as part of the Unity Authorized Course for
                game developer training.
              </p>
              <p class="highlighted">Utilized: C#, Unity</p>
              <p>How to play:</p>
              <ul>
                <li>Press Spacebar to start the game</li>
                <li>Use W, A, S, D keys to move your spaceship</li>
                <li>Click the left mouse button to shoot</li>
                <li>Collect power-ups and see how long you can survive!</li>
              </ul>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink(
                  'https://yosephmartin.com/demo/space-shooter/index.html'
                )
              "
              >Play the game!
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 4 -->
          <info-card id="prolog-project">
            <h2 slot="title">ProLog</h2>
            <p slot="tagline">
              Biometric authentication application that brings a peace of mind
              to your data security
            </p>
            <b-img
              center
              slot="custom-image"
              :src="prologImage"
              fluid
              alt="ProLog"
            ></b-img>
            <div slot="description">
              <p>
                ProLog is an Android app that adds an extra layer of security
                through two-factor authentication, combining your chosen method
                (Password, PIN, or Pattern) with biometric verification.
              </p>
              <p>
                After passing the first step, you’ll verify your identity using
                one of the following biometrics:
              </p>
              <ul>
                <li>Fingerprint</li>
                <li>Voice*</li>
                <li>Face recognition**</li>
              </ul>
              <p>
                If both checks are successful, you’ll gain access to your secure
                data
              </p>
              <p>
                * Voice authentication compares a customizable passphrase—not
                the unique characteristics of your voice
              </p>
              <p>** Face recognition will be available in a future update</p>
              <p class="highlighted">Utilized: Android, Java, XML</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="openLink('https://github.com/MartinezBlaze/ProLog')"
              >Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <!-- Learn more -->
      <b-row class="text-center">
        <b-col>
          <h2 class="mb-0">
            Curious about what I’ve been working on? Take a look at my
            portfolio!
          </h2>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="text-center">
          <router-link
            :to="{ name: 'softwareProjects' }"
            slot="button"
            tag="b-button"
            class="button-dark big-button"
          >
            Discover More
          </router-link>
        </b-col>
      </b-row>
    </content-section>
    <!-- KEEP IN TOUCH -->
    <content-section theme="dark">
      <keep-in-touch-section
        title="Let’s Connect!"
        subtitle="Got something cool in mind or just want to chat? Shoot me an email!"
      />
    </content-section>
    <!-- FOOTER -->
    <footer-section></footer-section>
  </div>
</template>

<script>
import InformationCard from "@/components/InformationCard";
import KeepInTouch from "../components/KeepInTouch.vue";
import ContentSection from "../components/ContentSection.vue";

export default {
  name: "AboutMe",
  components: {
    "info-card": InformationCard,
    "keep-in-touch-section": KeepInTouch,
    "content-section": ContentSection,
  },
  data() {
    return {
      // Image source link
      yosephMartinChristianPrimaryPhoto: require("@/assets/common/images/personal/formal-0.jpg"),
      yosephMartinChristianSecondaryPhoto: require("@/assets/common/images/personal/formal-1.jpg"),

      // Technical Know-how source link
      websiteDevelopmentImage: require("@/assets/common/images/icon/technical-skills/website-development.png"),
      mobileApplicationDevelopmentImage: require("@/assets/common/images/icon/technical-skills/mobile-application-development.png"),
      desktopApplicationDevelopmentImage: require("@/assets/common/images/icon/technical-skills/desktop-application-development.png"),
      databaseAndServerSideImage: require("@/assets/common/images/icon/technical-skills/database-and-server-side.png"),
      gameApplicationDevelopmentImage: require("@/assets/common/images/icon/technical-skills/game-application-development.png"),
      versionControlImage: require("@/assets/common/images/icon/technical-skills/version-control.png"),

      // Project source link
      stuffToDoImage: require("@/assets/common/images/project/stuff-to-do-1.png"),
      prologImage: require("@/assets/common/images/project/project-prolog-3.png"),

      // Carousel source link
      ymcCarousel: [
        {
          src: require("@/assets/common/images/project/ymc-0.png"),
        },
        {
          src: require("@/assets/common/images/project/ymc-1.png"),
        },
      ],
      galaxyShooterCarousel: [
        {
          src: require("@/assets/common/images/project/galaxy-shooter-1.png"),
        },
        {
          src: require("@/assets/common/images/project/galaxy-shooter-2.png"),
        },
      ],
    };
  },
  methods: {
    openLink(link) {
      window.open(link);
    },
  },
};
</script>

<style scoped>
img.personal-image {
  width: 225px;
}

img.center-personal-image {
  display: block;
  box-shadow: 7px 7px 0 0 rgba(255, 255, 255, 0.5);
  margin: 10px auto;
}

.academic-section-title {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid black;
}

.skill-table,
.skill-table th,
.skill-table td {
  border: 1px solid black;
  width: 325px;
  padding: 0.75rem;
  margin-left: auto;
  margin-right: auto;
}

ul {
  list-style-position: inside;
}
</style>
